// Single source of truth for node tags (free-form categories like "tech", "ai").
// Tags are a lightweight multi-value property stored in objects.properties_json.tags
// (mirrors how `lifecycle` is persisted). Suggestions live here; users can add custom
// tags beyond this list, and any tag already in use becomes a suggestion via the UI.

export const SUGGESTED_TAGS = [
  "tech",
  "ai",
  "design",
  "research",
  "learning",
  "product",
  "business",
  "psychology",
  "productivity"
];

// Normalize a typed tag to a stable, readable form: trimmed, lowercased, single-spaced,
// capped in length. Unlike status slugs we keep spaces (tags read as words/phrases).
export function normalizeTag(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .slice(0, 40);
}

// Merge a tag into a list (normalized, de-duplicated, order-preserving).
export function addTag(tags: string[], label: string): string[] {
  const next = normalizeTag(label);
  if (!next || tags.includes(next)) {
    return tags;
  }
  return [...tags, next];
}

export function removeTag(tags: string[], tag: string): string[] {
  return tags.filter((value) => value !== tag);
}

// Deterministic palette so the same tag always gets the same chip color.
const TAG_PALETTE: Array<{ tone: string; dotColor: string }> = [
  { tone: "bg-sky-100 text-sky-700 border-sky-200", dotColor: "#0ea5e9" },
  { tone: "bg-violet-100 text-violet-700 border-violet-200", dotColor: "#8b5cf6" },
  { tone: "bg-emerald-100 text-emerald-700 border-emerald-200", dotColor: "#10b981" },
  { tone: "bg-amber-100 text-amber-700 border-amber-200", dotColor: "#f59e0b" },
  { tone: "bg-rose-100 text-rose-700 border-rose-200", dotColor: "#f43f5e" },
  { tone: "bg-teal-100 text-teal-700 border-teal-200", dotColor: "#14b8a6" },
  { tone: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200", dotColor: "#d946ef" },
  { tone: "bg-indigo-100 text-indigo-700 border-indigo-200", dotColor: "#6366f1" }
];

function hashTag(tag: string): number {
  let hash = 0;
  for (let i = 0; i < tag.length; i += 1) {
    hash = (hash * 31 + tag.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function tagTone(tag: string): string {
  return TAG_PALETTE[hashTag(tag) % TAG_PALETTE.length].tone;
}

export function tagColor(tag: string): string {
  return TAG_PALETTE[hashTag(tag) % TAG_PALETTE.length].dotColor;
}

// Read a tags array off a raw properties bag (defensive against non-array values).
export function readTags(properties?: Record<string, unknown>): string[] {
  const raw = properties?.tags;
  return Array.isArray(raw) ? raw.filter((value): value is string => typeof value === "string") : [];
}
